/**
 * @fileoverview Servicio para gestión de sociedades
 * @description Maneja todas las peticiones HTTP relacionadas con sociedades
 */

import { API_BASE_URL, defaultConfig, handleResponse, buildApiUrl, API_ENDPOINTS } from '../config/api';

/**
 * Servicio para gestión de sociedades
 */
export const sociedadService = {
  /**
   * Crea una nueva sociedad
   * @param data - Datos de la sociedad
   * @returns Promise con la respuesta del servidor
   */
  async crearSociedad(data: any) {
    // Crear FormData para enviar archivos
    const formData = new FormData();
    
    // Agregar campos de texto
    formData.append('razonSocial', data.razonSocial);
    formData.append('cif', data.cif);
    formData.append('direccion', data.direccion);
    formData.append('localidad', data.localidad);
    formData.append('provincia', data.provincia);
    formData.append('codigoPostal', data.codigoPostal);
    formData.append('personaContacto', data.personaContacto);
    formData.append('telefono', data.telefono);
    formData.append('email', data.email);
    formData.append('condicionesIdAsociacion', data.condicionesIdAsociacion);
    
    // Agregar archivos
    if (data.fileResolucion) {
      formData.append('fileResolucion', data.fileResolucion);
    }
    if (data.fileTextoSeguros) {
      formData.append('fileTextoSeguros', data.fileTextoSeguros);
    }
    
    const response = await fetch(buildApiUrl(API_ENDPOINTS.SOCIEDAD), {
      method: 'POST',
      headers: {
        // NO incluir Content-Type - el navegador lo configura automáticamente para FormData
      },
      body: formData,
    });
    
    // Manejar específicamente el caso 409 (Conflict) - sociedad ya existe
    if (response.status === 409) {
      const conflictData = await response.json();
      return conflictData; // Retornar los datos sin lanzar excepción
    }
    
    // Para otros errores, usar handleResponse normal
    return handleResponse(response);
  },

  /**
   * Actualiza una sociedad existente
   * @param id - ID de la sociedad a actualizar
   * @param data - Datos actualizados de la sociedad
   * @returns Promise con la respuesta del servidor
   */
  async actualizarSociedad(id: number, data: any) {
    // Crear FormData para enviar archivos
    const formData = new FormData();
    
    // Asegurar que condicionesIdAsociacion se envíe correctamente
    const condicionesIdAsociacion = data.condicionesIdAsociacion !== undefined && data.condicionesIdAsociacion !== null
      ? String(data.condicionesIdAsociacion)
      : '';
    
    // Agregar campos actualizables (siempre se deben enviar, son obligatorios)
    formData.append('telefono', String(data.telefono || ''));
    formData.append('personaContacto', String(data.personaContacto || ''));
    formData.append('email', String(data.email || ''));
    formData.append('condicionesIdAsociacion', condicionesIdAsociacion);
    formData.append('direccion', String(data.direccion || ''));
    formData.append('localidad', String(data.localidad || ''));
    formData.append('provincia', String(data.provincia || ''));
    formData.append('codigoPostal', String(data.codigoPostal || ''));
    
    // Campos requeridos para crear la carpeta de archivos
    formData.append('razonSocial', String(data.razonSocial || ''));
    formData.append('cif', String(data.cif || ''));
    
    // Agregar archivos si están presentes
    if (data.fileResolucion && data.fileResolucion instanceof File) {
      formData.append('fileResolucion', data.fileResolucion);
    }
    if (data.fileTextoSeguros && data.fileTextoSeguros instanceof File) {
      formData.append('fileTextoSeguros', data.fileTextoSeguros);
    }
    
    const response = await fetch(buildApiUrl(`${API_ENDPOINTS.SOCIEDAD}/${id}`), {
      method: 'PUT',
      headers: {
        // NO incluir Content-Type - el navegador lo configura automáticamente para FormData
      },
      body: formData,
    });
    
    const result = await handleResponse(response);
    
    return result;
  },

  /**
   * Obtiene una sociedad por CIF
   * @param cif - CIF de la sociedad
   * @returns Promise con los datos de la sociedad
   */
  async obtenerSociedad(cif: string) {
    const response = await fetch(`${API_BASE_URL}/formulario/sociedad/${cif}`, {
      method: 'GET',
      ...defaultConfig,
    });
    return handleResponse(response);
  },
};
